import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { sideNavLayout, useSideNavCollapsed } from "../../helper/hooks/useSideNavLayout";
import {
  Box, Typography, IconButton, TextField, CircularProgress,
  Avatar, Tooltip, Fade,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Sparkle, PaperPlaneTilt, X, Trash, Plus, ClockCounterClockwise, Minus, SidebarSimple, AppWindow,
  PushPin, PencilSimple, ArrowRight,
} from '@phosphor-icons/react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../utils/config';
import ConfirmDialog from '../shared/ConfirmDialog';
import SearchBar from '../shared/SearchBar';
import { GOLD_SEARCH_SX } from '../shared/goldTheme';
import { notifyDeleted, notifyActionError } from '../../helper/notify';
import MarkdownContent from './ChatMarkdown';

/* Cream + gold chat panel — dark gold-brown header with a gold wave edge,
   gold user bubbles, cream bot bubbles (same palette as the rest of the app). */
const C_LIGHT = {
  accent:      '#b8892c',
  accentLight: 'rgba(184,137,44,0.10)',
  accentGlow:  'rgba(184,137,44,0.22)',
  coral:       '#c8342a',
  bg:          '#fffcf7',
  surface:     '#fffaf0',
  border:      '#ecdcb4',
  text:        '#1d1a14',
  muted:       '#6b6459',
  userBubble:  'linear-gradient(180deg,#f8cb62 0%,#f0b43c 100%)',
  userText:    '#1d1a14',
  aiBubble:    '#fdf6e6',
  aiText:      '#1d1a14',
  gold:        '#d8b25a',
  goldBtn:     'linear-gradient(180deg,#c9993a 0%,#a8791f 100%)',
};

const LOGO         = '/assests/website-logo.png';
const WELCOME      = `👋 Hi! I'm your MarketSync AI assistant.\n\nAsk me anything about the platform — modules, features, how to use them, or subscription help!`;
const MIN_W        = 280;
const MIN_H        = 300;
const MAX_W        = 740;
const MAX_H        = 860;
const GEOMETRY_KEY = 'marketsync_chatbot_geometry';
// Docked mode: the panel sits pinned to the right edge and the page makes room for it.
const DOCK_KEY       = 'marketsync_chatbot_docked';
const DOCK_WIDTH_KEY = 'marketsync_chatbot_dock_width';
const DOCK_MIN_W     = 280;
const DOCK_MAX_W     = 420;
const DOCK_DEFAULT_W = 340;
const DOCK_GAP       = 12; // space between the docked panel and the window edges
// Opened from the round button in the bottom-right corner: a floating panel in that corner, over the page.
const CORNER_W       = 400;
const CORNER_H       = 660;
const CORNER_GAP     = 24;  // from the right / bottom window edge
const CORNER_BOTTOM  = 96;  // bottom edge of the panel — just above the round button

/* Compact "time ago" for the recent-chats list */
const timeAgo = (d) => {
  if (!d) return '';
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)  return `${days}d ago`;
  return new Date(d).toLocaleDateString();
};

/* ── Sub-components ──────────────────────────────────────────── */
function TypingDots({ palette }) {
  return (
    <Box sx={{ display: 'flex', gap: '5px', alignItems: 'center', px: 1, py: 0.5 }}>
      {[0, 1, 2].map(i => (
        <Box key={i} sx={{
          width: 7, height: 7, borderRadius: '50%',
          background: palette.accent, opacity: 0.7,
          animation: 'typingBounce 1.2s ease-in-out infinite',
          animationDelay: `${i * 0.2}s`,
        }} />
      ))}
    </Box>
  );
}

function AIAvatar({ palette }) {
  return (
    <Avatar sx={{
      width: 38, height: 38, flexShrink: 0,
      background: 'radial-gradient(circle at 35% 30%, #3b301c 0%, #14110c 72%)',
      border: `2px solid ${palette.gold}`, p: '6px',
      boxShadow: '0 2px 8px rgba(20,17,12,0.25)',
    }}>
      <Box component="img" src={LOGO} alt="MarketSync AI"
        sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
        onError={e => { e.currentTarget.style.display = 'none'; }}
      />
    </Avatar>
  );
}

function UserAvatar({ user, palette }) {
  const name     = user?.name || user?.username || 'U';
  const pic      = user?.profilePicture;
  const initials = name.charAt(0).toUpperCase();
  return (
    <Avatar
      src={pic || undefined}
      sx={{
        width: 36, height: 36, flexShrink: 0,
        background: pic ? 'transparent' : palette.userBubble,
        fontSize: '0.82rem', fontWeight: 800, color: palette.userText,
        border: `2px solid ${palette.border}`,
      }}
    >
      {!pic && initials}
    </Avatar>
  );
}

/* Escape a string for safe use inside a RegExp */
const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/* Link button — shows the module's NAME; clicking opens that page (and the sidebar highlights it). */
const LinkButton = ({ label, path, onNavigate }) => (
  <Box
    component="button"
    onClick={() => onNavigate(path)}
    title={path}
    sx={{
      display: 'inline-flex', alignItems: 'center', gap: '3px',
      mx: '2px', px: '9px', py: '2px',
      borderRadius: '20px', border: '1px solid rgba(228,135,14,0.35)',
      background: 'rgba(228,135,14,0.10)', color: '#e4870e',
      fontSize: '0.85em', fontWeight: 700,
      cursor: 'pointer', verticalAlign: 'baseline',
      '&:hover': { background: 'rgba(228,135,14,0.20)' },
    }}
  >
    {label} ↗
  </Box>
);

/* Turn links in the assistant's text into in-app buttons. Two forms:
     [[Settings|/admin/settings?tab=users&side=customer]]   → button "Settings", opens that page on that tab
     /admin/balances                                         → button with the module's name ("Balances")
   Only the user's OWN accessible module paths are ever linked (modulePaths is their own Redux module list, with a
   `labels` map path → module name), so a clicked link is permission-safe. A path may carry ?tab=…&sub=… — the
   page opens on that tab (helper/useDeepLink.js). */
const baseOf = (path) => String(path).split('?')[0];
function linkifyModulePaths(text, modulePaths, onNavigate) {
  if (!modulePaths.length) return text;
  const labels = modulePaths.labels || {};
  const allowed = new Set(modulePaths);
  const sorted = [...modulePaths].sort((a, b) => b.length - a.length);
  const rawPath = `(?:${sorted.map(escapeRegExp).join('|')})(?:\\?[A-Za-z0-9_=&.\\-]*[A-Za-z0-9_=])?`;
  // group 1/2 = [[label|path]],  group 3 = a bare path
  const regex = new RegExp(`\\[\\[([^\\]|]+)\\|([^\\]]+)\\]\\]|(${rawPath})`, 'g');
  const out = [];
  let last = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    const hit = m[0];
    if (m[3]) {
      // a bare path must end there: "/admin/orders-old" must not light up "/admin/orders"
      const next = text.charAt(m.index + hit.length);
      if (next && /[A-Za-z0-9_-]/.test(next) && !hit.includes('?')) continue;
    }
    if (m.index > last) out.push(<React.Fragment key={`t${last}`}>{text.slice(last, m.index)}</React.Fragment>);
    if (m[3]) {
      out.push(<LinkButton key={`l${m.index}`} label={labels[baseOf(m[3])] || m[3]} path={m[3]} onNavigate={onNavigate} />);
    } else {
      const path = m[2].trim();
      // never link a page the user can't open — show just the label
      out.push(allowed.has(baseOf(path))
        ? <LinkButton key={`l${m.index}`} label={m[1].trim()} path={path} onNavigate={onNavigate} />
        : <React.Fragment key={`l${m.index}`}>{m[1].trim()}</React.Fragment>);
    }
    last = m.index + hit.length;
  }
  if (last === 0) return text;
  if (last < text.length) out.push(<React.Fragment key={`t${last}`}>{text.slice(last)}</React.Fragment>);
  return out;
}

/* The assistant's reply as rich text (headings, lists, tables, bold, code on a black background) — see ChatMarkdown.jsx.
   Module paths inside the text still become link buttons. */
function renderAssistantContent(content, modulePaths, onNavigate) {
  return <MarkdownContent text={content} inline={(t) => linkifyModulePaths(t, modulePaths, onNavigate)} />;
}

function Bubble({ msg, user, palette, modulePaths, onNavigate }) {
  const isUser = msg.role === 'user';
  const content = isUser ? msg.content : renderAssistantContent(msg.content, modulePaths, onNavigate);
  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: 1.2,
      flexDirection: isUser ? 'row-reverse' : 'row',
      mb: 2,
    }}>
      {isUser ? <UserAvatar user={user} palette={palette} /> : <AIAvatar palette={palette} />}
      <Box sx={{
        maxWidth: '76%',
        background: isUser ? palette.userBubble : palette.aiBubble,
        color: isUser ? palette.userText : palette.aiText,
        px: 2, py: 1.3,
        borderRadius: '16px',
        fontSize: '0.86rem',
        lineHeight: 1.6,
        whiteSpace: isUser ? 'pre-wrap' : 'normal', // the assistant's blocks (paragraphs, lists …) handle their own line breaks
        minWidth: 0, overflowWrap: 'anywhere',
        border: isUser ? '1px solid rgba(184,137,44,0.35)' : `1px solid ${palette.border}`,
        boxShadow: isUser
          ? '0 3px 10px rgba(184,137,44,0.22)'
          : '0 2px 8px rgba(122,87,18,0.07)',
      }}>
        {content}
      </Box>
    </Box>
  );
}

/* ── Main Component ──────────────────────────────────────────── */
// hideTriggerOnMobile — hides only the "Ask AI" pill on phones (it moves into
// the navbar's profile menu there); the chat panel itself still opens.
export default function ChatBot({ open: openProp, onOpenChange, hideTriggerOnMobile = false } = {}) {
  const [openState, setOpenState] = useState(false);
  const open = openProp !== undefined ? openProp : openState;
  const setOpen = useCallback((next) => {
    setOpenState((prev) => {
      const value = typeof next === 'function' ? next(prev) : next;
      if (onOpenChange) onOpenChange(value);
      return value;
    });
  }, [onOpenChange]);
  const [input,    setInput]    = useState('');
  const [messages, setMessages] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [minimized, setMinimized] = useState(false); // header-only collapsed panel
  const [cornerMode, setCornerMode] = useState(false); // opened from the corner button (floats over the page, never docked)
  const [docked, setDocked] = useState(() => {
    try { return localStorage.getItem(DOCK_KEY) === '1'; } catch { return false; }
  });
  const [dockW, setDockW] = useState(() => {
    try {
      const n = Number(localStorage.getItem(DOCK_WIDTH_KEY));
      return Number.isFinite(n) && n > 0 ? Math.min(DOCK_MAX_W, Math.max(DOCK_MIN_W, n)) : DOCK_DEFAULT_W;
    } catch { return DOCK_DEFAULT_W; }
  });
  const [awaitingFirst, setAwaitingFirst] = useState(false); // true until the first streamed token arrives
  const [fetching, setFetching] = useState(false);
  const [hasOlderHistory, setHasOlderHistory] = useState(false);
  const [loadingOlder,    setLoadingOlder]    = useState(false);
  // Always the white/colorful side-panel look — no theme toggle here.
  const palette = C_LIGHT;

  // ── Conversations (recent chats: new / switch / rename / pin / delete) ──
  const [activeConversation, setActiveConversation] = useState(null); // { _id, title, pinned, lastMessageAt }
  const [showConvoList, setShowConvoList] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [convoSearch,   setConvoSearch]   = useState('');
  const [loadingConvos, setLoadingConvos] = useState(false);
  const [renamingId,    setRenamingId]    = useState(null);
  const [renameValue,   setRenameValue]   = useState('');
  const [deleteTarget,  setDeleteTarget]  = useState(null); // conversation being confirmed for delete
  const [deletingConvo, setDeletingConvo] = useState(false);

  const bottomRef      = useRef(null);
  const inputRef       = useRef(null);
  const panelRef       = useRef(null);
  const messagesBoxRef = useRef(null);
  const triggerRef     = useRef(null);

  // Resize state in a ref — avoids React re-renders on every mousemove
  const resize = useRef({ on: false, startX: 0, startY: 0, originW: 0, originH: 0 });
  // Drag state, same ref pattern — lets the panel be grabbed by its header
  // and moved anywhere on the page, instead of always reopening anchored
  // under the trigger button.
  const drag = useRef({ on: false, startX: 0, startY: 0, originLeft: 0, originTop: 0 });
  const dockResize = useRef({ on: false });

  const token   = useSelector(s => s.auth.token);
  const user    = useSelector(s => s.auth.user);
  const modules = useSelector(s => s.auth.modules);
  const role    = useSelector(s => s.auth.role);
  // AI credits (plan allowance per month) — CUSTOMER side only; admin-side users never spend or see credits.
  const isCustomerUser = useSelector(s => s.auth.side) === 'customer';
  const [credits, setCredits] = useState(null); // { limit, used, remaining, planName } once loaded
  const navigate = useNavigate();

  // Only the user's OWN accessible module paths are ever linkified — this is
  // what keeps a clicked link permission-safe (the AI never sees paths
  // outside this list either, per the backend system prompt).
  const modulePaths = React.useMemo(() => {
    const list = (modules || []).filter(m => m.route_path);
    const paths = list.map(m => m.route_path);
    // path → module name, so a bare path in the answer is shown as the module's name, not as a URL
    paths.labels = Object.fromEntries(list.map(m => [m.route_path, m.name]));
    return paths;
  }, [modules]);
  const handleNavigateToModule = useCallback((path) => {
    navigate(path);
    setOpen(false);
  }, [navigate, setOpen]);

  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
  const dockedNow = docked && !isNarrow && !cornerMode; // docked layout is in effect
  const dockActive = open && dockedNow;            // docked AND visible → the page makes room

  // The docked chat and the left sidebar never share the screen:
  //  · the chat docks (or opens docked)  → the sidebar folds to its narrow rail;
  //  · the sidebar is opened again while the chat is docked → the chat closes.
  const sideCollapsed = useSideNavCollapsed();
  const wasDockActive = useRef(false);
  const wasSideCollapsed = useRef(sideCollapsed);
  useEffect(() => {
    if (dockActive && !wasDockActive.current) sideNavLayout.setCollapsed(true);
    wasDockActive.current = dockActive;
  }, [dockActive]);
  useEffect(() => {
    if (wasSideCollapsed.current && !sideCollapsed && dockActive) setOpen(false);
    wasSideCollapsed.current = sideCollapsed;
  }, [sideCollapsed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Remember dock choice + width.
  useEffect(() => { try { localStorage.setItem(DOCK_KEY, docked ? '1' : '0'); } catch { /* storage unavailable */ } }, [docked]);
  useEffect(() => { try { localStorage.setItem(DOCK_WIDTH_KEY, String(dockW)); } catch { /* storage unavailable */ } }, [dockW]);

  // The page layouts (AdminLayout / layout) add this much right padding, so
  // the content shrinks beside the docked panel and grows back when it closes.
  useEffect(() => {
    if (!dockActive) return undefined;
    document.body.style.setProperty('--ChatDock-width', `${dockW + DOCK_GAP * 2}px`);
    return () => { document.body.style.removeProperty('--ChatDock-width'); };
  }, [dockActive, dockW]);

  /* ── Global mouse handlers for resize AND drag ── */
  useEffect(() => {
    const onMove = (e) => {
      const panel = panelRef.current;
      if (!panel) return;
      if (dockResize.current.on) {
        const maxW = Math.min(DOCK_MAX_W, Math.round(window.innerWidth * 0.6));
        setDockW(Math.min(maxW, Math.max(DOCK_MIN_W, window.innerWidth - e.clientX - DOCK_GAP)));
        return;
      }
      if (resize.current.on) {
        const newW = Math.min(MAX_W, Math.max(MIN_W, resize.current.originW + e.clientX - resize.current.startX));
        const newH = Math.min(MAX_H, Math.max(MIN_H, resize.current.originH + e.clientY - resize.current.startY));
        panel.style.width  = `${newW}px`;
        panel.style.height = `${newH}px`;
      } else if (drag.current.on) {
        const panelW = panel.offsetWidth;
        const panelH = panel.offsetHeight;
        const newLeft = Math.max(4, Math.min(window.innerWidth  - panelW - 4, drag.current.originLeft + e.clientX - drag.current.startX));
        const newTop  = Math.max(4, Math.min(window.innerHeight - panelH - 4, drag.current.originTop  + e.clientY - drag.current.startY));
        panel.style.left   = `${newLeft}px`;
        panel.style.top    = `${newTop}px`;
        panel.style.right  = 'auto';
        panel.style.bottom = 'auto';
      }
    };

    const onUp = () => {
      if (resize.current.on && panelRef.current) {
        const r = panelRef.current.getBoundingClientRect();
        try {
          const prev = JSON.parse(localStorage.getItem(GEOMETRY_KEY) || 'null') || {};
          localStorage.setItem(GEOMETRY_KEY, JSON.stringify({ ...prev, width: r.width, height: r.height }));
        } catch { /* localStorage unavailable — size just won't persist */ }
      }
      if (drag.current.on && panelRef.current) {
        const r = panelRef.current.getBoundingClientRect();
        try {
          const prev = JSON.parse(localStorage.getItem(GEOMETRY_KEY) || 'null') || {};
          localStorage.setItem(GEOMETRY_KEY, JSON.stringify({ ...prev, left: r.left, top: r.top, dragged: true }));
        } catch { /* localStorage unavailable — position just won't persist */ }
      }
      resize.current.on = false;
      drag.current.on   = false;
      dockResize.current.on = false;
      document.body.style.userSelect = '';
      document.body.style.cursor     = '';
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
    };
  }, []);

  /* ── Drag handle: start drag — grabbed from the panel's header ── */
  const onDragDown = useCallback((e) => {
    // Don't start a drag from the header's own buttons (close/minimize/etc).
    if (e.target.closest('button')) return;
    const panel = panelRef.current;
    if (!panel) return;
    const r = panel.getBoundingClientRect();
    drag.current = { on: true, startX: e.clientX, startY: e.clientY, originLeft: r.left, originTop: r.top };
    document.body.style.userSelect = 'none';
    document.body.style.cursor     = 'grabbing';
  }, []);

  /* ── Restore saved SIZE from a previous session (desktop only). Position
     is restored here too, but ONLY if the user has actually dragged the
     panel before (saved.dragged) — otherwise it keeps re-anchoring under
     the trigger button, below. ── */
  useEffect(() => {
    if (isMobile) return;
    const panel = panelRef.current;
    if (!panel) return;
    try {
      const saved = JSON.parse(localStorage.getItem(GEOMETRY_KEY) || 'null');
      if (!saved) return;
      if (saved.width)  panel.style.width  = `${Math.min(MAX_W, Math.max(MIN_W, saved.width))}px`;
      if (saved.height) panel.style.height = `${Math.min(MAX_H, Math.max(MIN_H, saved.height))}px`;
    } catch { /* ignore malformed/missing saved geometry */ }
  }, [isMobile]);

  /* ── Anchor the panel directly under the trigger button every time it
     opens (desktop only — on mobile the panel is already full-width via sx,
     so left/right placement doesn't matter there). Runs in useLayoutEffect
     so the position is set before paint — no visible jump from a stale spot
     to the button. Skipped once the user has dragged the panel at least
     once — from then on it reopens wherever they last left it instead. ── */
  useLayoutEffect(() => {
    if (!open || isMobile) return;
    // Opened from the corner button → always the bottom-right corner, sized like the chat window you know
    // (anchored with left/top so the drag and resize handles work as usual).
    if (cornerMode) {
      const cp = panelRef.current;
      if (cp) {
        const w = Math.min(CORNER_W, window.innerWidth - CORNER_GAP * 2);
        const h = Math.min(CORNER_H, window.innerHeight - CORNER_BOTTOM - 16);
        cp.style.width  = `${w}px`;
        cp.style.height = `${h}px`;
        cp.style.left   = `${Math.max(8, window.innerWidth - CORNER_GAP - w)}px`;
        cp.style.top    = `${Math.max(8, window.innerHeight - CORNER_BOTTOM - h)}px`;
        cp.style.right  = 'auto';
        cp.style.bottom = 'auto';
      }
      return;
    }
    let hasDraggedBefore = false;
    try {
      const saved = JSON.parse(localStorage.getItem(GEOMETRY_KEY) || 'null');
      if (saved?.dragged && Number.isFinite(saved.left) && Number.isFinite(saved.top)) {
        const panel = panelRef.current;
        if (panel) {
          const panelW = panel.offsetWidth  || 340;
          const panelH = panel.offsetHeight || 480;
          panel.style.left   = `${Math.max(4, Math.min(saved.left, window.innerWidth  - panelW - 4))}px`;
          panel.style.top    = `${Math.max(4, Math.min(saved.top,  window.innerHeight - panelH - 4))}px`;
          panel.style.right  = 'auto';
          panel.style.bottom = 'auto';
          hasDraggedBefore = true;
        }
      }
    } catch { /* ignore malformed/missing saved geometry */ }
    if (hasDraggedBefore) return;
    const panel = panelRef.current;
    const btn   = triggerRef.current;
    if (!panel || !btn) return;
    const btnRect = btn.getBoundingClientRect();
    const panelW  = panel.offsetWidth  || 340;
    const panelH  = panel.offsetHeight || 480;
    const gap = 10; // small visible gap between the button and the panel's top edge
    const left = Math.max(8, Math.min(btnRect.left, window.innerWidth  - panelW - 8));
    const top  = Math.max(8, Math.min(btnRect.bottom + gap, window.innerHeight - panelH - 8));
    panel.style.left   = `${left}px`;
    panel.style.top    = `${top}px`;
    panel.style.right  = 'auto';
    panel.style.bottom = 'auto';
  }, [open, isMobile, cornerMode]);

  /* ── Docked mode: drag the panel's left edge to make it wider / narrower ── */
  const onDockResizeDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dockResize.current = { on: true };
    document.body.style.userSelect = 'none';
    document.body.style.cursor     = 'ew-resize';
  }, []);

  /* ── Resize handle: start resize ── */
  const onResizeDown = useCallback((e) => {
    e.stopPropagation();
    const panel = panelRef.current;
    if (!panel) return;
    const r = panel.getBoundingClientRect();
    resize.current = { on: true, startX: e.clientX, startY: e.clientY, originW: r.width, originH: r.height };
    document.body.style.userSelect = 'none';
    document.body.style.cursor     = 'nwse-resize';
  }, []);

  /* ── Chat logic ── */
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const loadHistory = useCallback(async (conversationId) => {
    if (!conversationId) return;
    setFetching(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/chat/history`, {
        params: { conversationId },
        headers: { Authorization: `Bearer ${token}` },
      });
      const msgs = data.messages || [];
      setMessages(msgs.length ? msgs : [{ role: 'assistant', content: WELCOME, _id: 'welcome' }]);
      setHasOlderHistory(!!data.hasMore);
    } catch (err) {
      console.error('[ChatBot] loadHistory failed:', err.response?.data || err.message);
      const failMsg = err.response?.status === 401
        ? 'Your session has expired — please log in again to see your chat history.'
        : "Couldn't load your previous chat history. You can still start chatting below.";
      setMessages([{ role: 'assistant', content: failMsg, _id: 'welcome' }]);
      setHasOlderHistory(false);
    } finally {
      setFetching(false);
    }
  }, [token]);

  // On open, pick (or create) the most recently used conversation.
  useEffect(() => {
    if (!open || !token || activeConversation) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/api/chat/active`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setActiveConversation(data.conversation);
      } catch (err) {
        console.error('[ChatBot] loadActiveConversation failed:', err.response?.data || err.message);
      }
    })();
  }, [open, token, activeConversation]);

  // Load that conversation's messages whenever it changes.
  useEffect(() => {
    if (activeConversation?._id) loadHistory(activeConversation._id);
  }, [activeConversation?._id, loadHistory]);

  const loadOlderHistory = useCallback(async () => {
    if (loadingOlder || !hasOlderHistory || !activeConversation?._id) return;
    const oldest = messages.find(m => m._id !== 'welcome' && m.createdAt);
    if (!oldest) { setHasOlderHistory(false); return; }
    setLoadingOlder(true);
    const box = messagesBoxRef.current;
    const prevScrollHeight = box?.scrollHeight || 0;
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/chat/history`, {
        params: { conversationId: activeConversation._id, before: oldest.createdAt },
        headers: { Authorization: `Bearer ${token}` },
      });
      const older = data.messages || [];
      setMessages(prev => [...older, ...prev]);
      setHasOlderHistory(!!data.hasMore);
      // Keep the viewport anchored on the same message instead of jumping to the top
      requestAnimationFrame(() => {
        if (box) box.scrollTop = box.scrollHeight - prevScrollHeight;
      });
    } catch (err) {
      console.error('[ChatBot] loadOlderHistory failed:', err.response?.data || err.message);
    } finally {
      setLoadingOlder(false);
    }
  }, [token, loadingOlder, hasOlderHistory, messages, activeConversation]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || loading || !activeConversation?._id) return;

    setInput('');
    setMessages(prev => [...prev.filter(m => m._id !== 'welcome'), { role: 'user', content: text, _id: Date.now() }]);
    setLoading(true);
    setAwaitingFirst(true);

    const assistantId = Date.now() + 1;
    let streamedAny = false;

    try {
      const res = await fetch(`${API_BASE_URL}/api/chat/send`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify({ message: text, modules, role, conversationId: activeConversation._id }),
      });

      if (res.status === 402) {
        // out of AI credits: the server says why (and what is left) — show it as the assistant's answer
        const j = await res.json().catch(() => ({}));
        if (j.credits) setCredits({ ...j.credits, applies: true });
        setMessages(prev => [...prev, { role: 'assistant', content: j.error || 'You have no AI credits left this month.', _id: assistantId }]);
        return;
      }
      if (!res.ok || !res.body) {
        throw Object.assign(new Error('Chat request failed'), { response: { status: res.status } });
      }

      const reader  = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data:')) continue;
          const payload = trimmed.slice(5).trim();
          if (!payload || payload === '[DONE]') continue;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.credits) setCredits({ ...parsed.credits, applies: true }); // what is left after this answer
            if (parsed.delta) {
              if (!streamedAny) setAwaitingFirst(false);
              streamedAny = true;
              setMessages(prev => {
                const exists = prev.some(m => m._id === assistantId);
                return exists
                  ? prev.map(m => m._id === assistantId ? { ...m, content: m.content + parsed.delta } : m)
                  : [...prev, { role: 'assistant', content: parsed.delta, _id: assistantId }];
              });
            }
          } catch { /* ignore malformed SSE line */ }
        }
      }

      if (!streamedAny) {
        setMessages(prev => [...prev, { role: 'assistant', content: 'I could not generate a response. Please rephrase your question.', _id: assistantId }]);
      }
    } catch (err) {
      let errMsg = 'Sorry, something went wrong. Please try again.';
      if (!err.response) errMsg = 'Network error — check your connection and try again.';
      else if (err.response.status === 401) errMsg = 'Your session has expired. Please log in again.';
      else if (err.response.status === 429) errMsg = 'Too many requests — please wait a moment and try again.';
      setMessages(prev => [...prev, { role: 'assistant', content: errMsg, _id: assistantId }]);
    } finally {
      setLoading(false);
      setAwaitingFirst(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [input, loading, token, modules, role, activeConversation]);

  /* ── AI credits: load what is left whenever the chat opens (customers only) ── */
  useEffect(() => {
    if (!open || !isCustomerUser || !token) return;
    axios.get(`${API_BASE_URL}/api/chat/credits`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => { if (data?.applies) setCredits(data); })
      .catch(() => {});
  }, [open, isCustomerUser, token]);

  /* ── New chat ── */
  const handleNewChat = useCallback(async () => {
    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/chat/conversations`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActiveConversation(data.conversation);
      setMessages([{ role: 'assistant', content: WELCOME, _id: 'welcome' }]);
      setHasOlderHistory(false);
      setShowConvoList(false);
    } catch (err) {
      notifyActionError('create', 'Failed to start a new chat', err);
    }
  }, [token]);

  /* ── Recent chats list ── */
  const loadConversations = useCallback(async (search = '') => {
    setLoadingConvos(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/chat/conversations`, {
        params: search ? { search } : {},
        headers: { Authorization: `Bearer ${token}` },
      });
      setConversations(data.conversations || []);
    } catch (err) {
      notifyActionError('load', 'Failed to load recent chats', err);
    } finally {
      setLoadingConvos(false);
    }
  }, [token]);

  // Debounced — fires as soon as the list opens (search === '') and again
  // whenever the search box changes while it's open.
  useEffect(() => {
    if (!showConvoList) return;
    const t = setTimeout(() => loadConversations(convoSearch.trim()), 250);
    return () => clearTimeout(t);
  }, [showConvoList, convoSearch, loadConversations]);

  const handleToggleList = () => setShowConvoList(v => !v);

  const handleSelectConversation = (convo) => {
    setActiveConversation(convo);
    setShowConvoList(false);
  };

  const handleTogglePin = async (convo, e) => {
    e.stopPropagation();
    try {
      const { data } = await axios.patch(
        `${API_BASE_URL}/api/chat/conversations/${convo._id}`,
        { pinned: !convo.pinned },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConversations(prev => {
        const next = prev.map(c => (c._id === convo._id ? data.conversation : c));
        return [...next].sort((a, b) => (b.pinned - a.pinned) || (new Date(b.lastMessageAt) - new Date(a.lastMessageAt)));
      });
      if (activeConversation?._id === convo._id) setActiveConversation(data.conversation);
    } catch (err) {
      notifyActionError('update', 'Failed to update pin', err);
    }
  };

  const startRename = (convo, e) => {
    e.stopPropagation();
    setRenamingId(convo._id);
    setRenameValue(convo.title);
  };

  const submitRename = async (id) => {
    const title = renameValue.trim();
    setRenamingId(null);
    if (!title) return;
    try {
      const { data } = await axios.patch(
        `${API_BASE_URL}/api/chat/conversations/${id}`,
        { title },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConversations(prev => prev.map(c => (c._id === id ? data.conversation : c)));
      if (activeConversation?._id === id) setActiveConversation(data.conversation);
    } catch (err) {
      notifyActionError('rename', 'Failed to rename chat', err);
    }
  };

  const confirmDeleteConversation = async () => {
    if (!deleteTarget) return;
    setDeletingConvo(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/chat/conversations/${deleteTarget._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      notifyDeleted('Chat');
      setConversations(prev => prev.filter(c => c._id !== deleteTarget._id));
      if (activeConversation?._id === deleteTarget._id) {
        await handleNewChat();
      }
    } catch (err) {
      notifyActionError('delete', 'Failed to delete chat', err);
    } finally {
      setDeletingConvo(false);
      setDeleteTarget(null);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const userName = user?.name || user?.username || 'User';
  const userPic  = user?.profilePicture;

  const isAdminRole = role === 'super-admin' || role === 'superadmin' || role === 'admin';
  const suggestedQuestions = isAdminRole
    ? ['How do I add a new user?', 'How to sync all stores?', 'How to set up permissions?']
    : ['What modules can I access?', 'My trial expired, what to do?', 'How to sync orders?'];

  return (
    <>
      <style>{`
        @keyframes typingBounce {
          0%,60%,100% { transform:translateY(0); opacity:0.4; }
          30%          { transform:translateY(-5px); opacity:1; }
        }
        @keyframes chatPop {
          from { opacity:0; transform:scale(0.93) translateY(8px); }
          to   { opacity:1; transform:scale(1) translateY(0); }
        }
      `}</style>

      {/* ── Trigger Button ── */}
      <Tooltip title="Ask anything about MarketSync" placement="bottom">
        {/* Gold pill: dark round sparkle badge · divider · "Ask AI" +
            "Get instant help →". Phones show only the round badge. */}
        <Box
          ref={triggerRef}
          onClick={() => { setCornerMode(false); setOpen(p => !p); }}
          sx={{
            position: 'relative',
            display: hideTriggerOnMobile ? { xs: 'none', sm: 'inline-flex' } : 'inline-flex', alignItems: 'center', gap: { xs: 0, sm: 1.1 },
            p: '3px', pr: { xs: '3px', sm: 1.6 }, height: 44, borderRadius: '999px', cursor: 'pointer',
            background: 'linear-gradient(180deg,#fffaf0 0%,#fbefd6 100%)',
            border: `1.5px solid ${open ? '#b8892c' : '#e2b85c'}`,
            boxShadow: open
              ? '0 0 0 3px rgba(216,178,90,0.28), 0 6px 18px rgba(184,137,44,0.35)'
              : '0 0 14px rgba(232,170,60,0.38), 0 4px 12px rgba(184,137,44,0.16)',
            transition: 'all 0.2s ease',
            '&:hover': {
              boxShadow: '0 0 20px rgba(232,170,60,0.55), 0 6px 16px rgba(184,137,44,0.25)',
              transform: 'translateY(-1px)',
            },
            '&:hover .askai-arrow': { transform: 'translateX(3px)' },
          }}
        >
          <Box sx={{
            width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'radial-gradient(circle at 35% 30%, #3b301c 0%, #14110c 72%)',
            border: '1.5px solid #d8b25a',
            boxShadow: 'inset 0 0 8px rgba(216,178,90,0.3), 0 2px 6px rgba(20,17,12,0.35)',
          }}>
            <Sparkle size={19} weight="fill" color="#e9b949" />
          </Box>
          <Box sx={{ display: { xs: 'none', sm: 'block' }, width: '1px', height: 26, flexShrink: 0, background: 'rgba(184,137,44,0.45)' }} />
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: '0.86rem', fontWeight: 800, color: '#1d1a14', lineHeight: 1.15, whiteSpace: 'nowrap' }}>
              Ask AI
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontSize: '0.64rem', color: '#7d7466', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                Get instant help
              </Typography>
              <Box className="askai-arrow" sx={{ display: 'flex', transition: 'transform 0.2s ease' }}>
                <ArrowRight size={12} weight="bold" color="#e4870e" />
              </Box>
            </Box>
          </Box>
        </Box>
      </Tooltip>

      {/* ── Round Ask-AI button, bottom-right corner of the screen. Opens the chat in that corner, over the page
          (the nav "Ask AI" button and the side dock work as before). Hidden while the chat is docked, since the
          docked panel occupies that corner. ── */}
      {!dockActive && (
        <Tooltip title={open && cornerMode ? 'Close chat' : 'Ask AI'} placement="left">
          <Box
            onClick={() => { if (open) { setOpen(false); } else { setCornerMode(true); setOpen(true); } }}
            role="button"
            aria-label="Ask AI"
            sx={{
              position: 'fixed', right: CORNER_GAP, bottom: `var(--ChatFab-bottom, ${CORNER_GAP}px)`, zIndex: 1490,
              width: 56, height: 56, borderRadius: '50%', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'radial-gradient(circle at 35% 30%, #3b301c 0%, #14110c 72%)',
              border: '2px solid #d8b25a',
              boxShadow: '0 10px 28px rgba(20,17,12,0.45), 0 0 18px rgba(232,170,60,0.45), inset 0 0 8px rgba(216,178,90,0.3)',
              transition: 'transform 0.18s ease, box-shadow 0.18s ease, bottom 0.25s ease',
              '&:hover': { transform: 'scale(1.07)', boxShadow: '0 12px 32px rgba(20,17,12,0.5), 0 0 24px rgba(232,170,60,0.6), inset 0 0 8px rgba(216,178,90,0.3)' },
            }}
          >
            {open && cornerMode ? <X size={24} weight="bold" color="#e9b949" /> : <Sparkle size={26} weight="fill" color="#e9b949" />}
          </Box>
        </Tooltip>
      )}

      {/* ── Chat Panel — small popover anchored under the trigger button ── */}
      <Fade in={open}>
        <Box
          ref={panelRef}
          data-min={minimized ? 'true' : 'false'}
          data-docked={dockedNow ? 'true' : 'false'}
          sx={{
            position: 'fixed',
            top:   68,
            right: 20,
            /* Initial size — JS will override on resize/restore/anchor */
            width:  { xs: 'calc(100vw - 24px)', sm: '360px' },
            height: { xs: 'calc(100dvh - 88px)', sm: '480px' },
            minWidth:  `${MIN_W}px`,
            minHeight: `${MIN_H}px`,
            zIndex: 1500,
            display: open ? 'flex' : 'none',
            flexDirection: 'column',
            borderRadius: '22px',
            background: palette.bg,
            boxShadow: '0 24px 70px rgba(122,87,18,0.28), 0 4px 22px rgba(20,17,12,0.16)',
            border: `1.5px solid ${palette.gold}`,
            animation: 'chatPop 0.22s ease',
            overflow: 'hidden',
            // Minimized = header only. !important beats the inline height the
            // resize handle writes onto the element.
            '&[data-min="true"]': { height: 'auto !important', minHeight: '0 !important' },
            // Docked: pinned to the right edge, full height, width set by the left-edge handle.
            // !important beats the inline left/top/width/height the floating mode writes.
            '&[data-docked="true"]': {
              top: `${DOCK_GAP}px !important`,
              right: `${DOCK_GAP}px !important`,
              bottom: `${DOCK_GAP}px !important`,
              left: 'auto !important',
              width: `${dockW}px !important`,
              height: 'auto !important',
              maxWidth: `calc(100vw - ${DOCK_GAP * 2}px)`,
            },
            '&[data-docked="true"][data-min="true"]': { bottom: 'auto !important' },
          }}
        >

          {/* ── Header — dark gold-brown with a gold wave edge. Also the drag
              handle: grab anywhere on it (except its own buttons) to move the
              panel anywhere on the page. ── */}
          <Box
            onMouseDown={dockedNow ? undefined : onDragDown}
            title={dockedNow ? undefined : 'Drag to move'}
            sx={{
              position: 'relative',
              px: 2, pt: 1.5, pb: 4.2,
              background: 'linear-gradient(160deg, #14110c 0%, #281f10 55%, #3a2c12 100%)',
              flexShrink: 0,
              userSelect: 'none',
              cursor: dockedNow ? 'default' : 'grab',
              '&:active': { cursor: dockedNow ? 'default' : 'grabbing' },
            }}
          >
            {/* gold wave along the bottom edge */}
            <Box component="svg" viewBox="0 0 400 44" preserveAspectRatio="none" aria-hidden
              sx={{ position: 'absolute', left: 0, right: 0, bottom: -1, width: '100%', height: 40, pointerEvents: 'none' }}>
              <defs>
                <linearGradient id="chatWaveGold" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#8c6a1c" />
                  <stop offset="45%" stopColor="#e9c977" />
                  <stop offset="100%" stopColor="#b8892c" />
                </linearGradient>
              </defs>
              <path d="M0 26 C 70 40, 150 8, 250 22 S 360 38, 400 16 L400 44 L0 44 Z" fill="#fffaf0" />
              <path d="M0 26 C 70 40, 150 8, 250 22 S 360 38, 400 16" fill="none" stroke="url(#chatWaveGold)" strokeWidth="3" strokeLinecap="round" />
            </Box>

            <Box sx={{ position: 'relative', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', columnGap: 1, alignItems: 'center' }}>
              {/* left: logo + title + ONE-line status */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Box component="img" src={LOGO} alt="MarketSync"
                  sx={{ height: 32, width: 'auto', flexShrink: 0, objectFit: 'contain' }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: '1.08rem', fontWeight: 800, lineHeight: 1.15, color: '#ffffff' }}>
                    Market<Box component="span" sx={{ color: '#e9b949' }}>Sync</Box> AI
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, minWidth: 0 }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: '#34d399', flexShrink: 0, boxShadow: '0 0 6px rgba(52,211,153,0.7)' }} />
                    <Typography noWrap sx={{ fontSize: '0.66rem', color: 'rgba(255,255,255,0.78)', minWidth: 0 }}>
                      {isCustomerUser && credits
                        ? `Online · ${Number(credits.remaining).toLocaleString()} / ${Number(credits.limit).toLocaleString()} AI credits`
                        : 'Online · Always ready'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* right: icons on the first row, your name + photo underneath */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.7 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.1 }}>
                  <Tooltip title="New chat">
                    <IconButton onClick={handleNewChat} size="small" sx={{ p: 0.5, color: 'rgba(255,255,255,0.85)', '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.12)' } }}>
                      <Plus size={16} weight="bold" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Recent chats">
                    <IconButton
                      onClick={handleToggleList} size="small"
                      sx={{
                        p: 0.5,
                        color: showConvoList ? '#e9b949' : 'rgba(255,255,255,0.85)',
                        background: showConvoList ? 'rgba(233,185,73,0.18)' : 'transparent',
                        '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.12)' },
                      }}
                    >
                      <ClockCounterClockwise size={16} />
                    </IconButton>
                  </Tooltip>
                  {!isNarrow && (
                    <Tooltip title={dockedNow ? 'Float (move & resize freely)' : 'Dock to the right side'}>
                      <IconButton
                        onClick={() => { if (dockedNow) { setDocked(false); } else { setDocked(true); setCornerMode(false); } setMinimized(false); }} size="small"
                        sx={{
                          p: 0.5,
                          color: dockedNow ? '#e9b949' : 'rgba(255,255,255,0.85)',
                          background: dockedNow ? 'rgba(233,185,73,0.18)' : 'transparent',
                          '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.12)' },
                        }}
                      >
                        {dockedNow ? <AppWindow size={16} /> : <SidebarSimple size={16} />}
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={minimized ? 'Restore' : 'Minimize'}>
                    <IconButton onClick={() => setMinimized(m => !m)} size="small" sx={{ p: 0.5, color: 'rgba(255,255,255,0.85)', '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.12)' } }}>
                      <Minus size={16} weight="bold" />
                    </IconButton>
                  </Tooltip>
                  <IconButton onClick={() => { setMinimized(false); setOpen(false); }} size="small" sx={{ p: 0.5, color: 'rgba(255,255,255,0.85)', '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.12)' } }}>
                    <X size={16} weight="bold" />
                  </IconButton>
                </Box>

                {/* your name + photo (no arrow) */}
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.8, pl: 0.4, pr: 1.2, py: 0.35, maxWidth: '100%',
                  borderRadius: '999px', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(233,185,73,0.45)',
                }}>
                  <Avatar
                    src={userPic || undefined}
                    sx={{
                      width: 26, height: 26,
                      background: userPic ? 'transparent' : palette.userBubble,
                      fontSize: '0.66rem', fontWeight: 800, color: palette.userText,
                      border: '1.5px solid rgba(233,185,73,0.8)',
                    }}
                  >
                    {!userPic && userName.charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography noWrap sx={{ fontSize: '0.74rem', color: '#ffffff', fontWeight: 600, maxWidth: 110 }}>
                    {userName.split(' ')[0]}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {minimized ? null : showConvoList ? (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: palette.surface }}>
              {/* ── Search recent chats ── */}
              <Box sx={{ p: 1.2, flexShrink: 0 }}>
                <SearchBar
                  value={convoSearch}
                  onChange={e => setConvoSearch(e.target.value)}
                  placeholder="Search your chats..."
                  sx={{ ...GOLD_SEARCH_SX, borderRadius: '999px' }}
                  inputProps={{ autoFocus: true }}
                />
              </Box>

              {/* ── List ── */}
              <Box sx={{
                flex: 1, overflowY: 'auto', px: 1, pb: 1,
                '&::-webkit-scrollbar': { width: '4px' },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(184,137,44,0.28)', borderRadius: '4px' },
              }}>
                {loadingConvos ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                    <CircularProgress size={22} sx={{ color: palette.accent }} />
                  </Box>
                ) : conversations.length === 0 ? (
                  <Box sx={{ textAlign: 'center', pt: 4, px: 2 }}>
                    <Typography sx={{ fontSize: '0.78rem', color: palette.muted }}>
                      {convoSearch ? 'No chats match your search' : 'No saved chats yet — start one!'}
                    </Typography>
                  </Box>
                ) : (
                  conversations.map(convo => {
                    const isActive = activeConversation?._id === convo._id;
                    const isRenaming = renamingId === convo._id;
                    return (
                      <Box
                        key={convo._id}
                        onClick={() => !isRenaming && handleSelectConversation(convo)}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 0.6,
                          px: 1.2, py: 0.9, mb: 0.5, borderRadius: '10px', cursor: 'pointer',
                          background: isActive ? palette.accentLight : 'transparent',
                          border: `1px solid ${isActive ? palette.accent : 'transparent'}`,
                          transition: 'background 0.14s',
                          '&:hover': { background: palette.accentLight },
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {isRenaming ? (
                            <TextField
                              autoFocus size="small" value={renameValue}
                              onClick={e => e.stopPropagation()}
                              onChange={e => setRenameValue(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') submitRename(convo._id); if (e.key === 'Escape') setRenamingId(null); }}
                              onBlur={() => submitRename(convo._id)}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  fontSize: '0.8rem', background: palette.bg, color: palette.text,
                                  '& fieldset': { borderColor: palette.accent },
                                },
                              }}
                            />
                          ) : (
                            <>
                              <Typography noWrap sx={{ fontSize: '0.8rem', fontWeight: isActive ? 700 : 500, color: palette.text }}>
                                {convo.title}
                              </Typography>
                              <Typography sx={{ fontSize: '0.64rem', color: palette.muted }}>
                                {timeAgo(convo.lastMessageAt)}
                              </Typography>
                            </>
                          )}
                        </Box>
                        {!isRenaming && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.1, flexShrink: 0 }}>
                            <Tooltip title={convo.pinned ? 'Unpin' : 'Pin'}>
                              <IconButton size="small" onClick={(e) => handleTogglePin(convo, e)} sx={{ color: convo.pinned ? palette.accent : palette.muted, p: 0.4 }}>
                                <PushPin size={13} weight={convo.pinned ? 'fill' : 'regular'} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Rename">
                              <IconButton size="small" onClick={(e) => startRename(convo, e)} sx={{ color: palette.muted, p: 0.4 }}>
                                <PencilSimple size={13} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDeleteTarget(convo); }} sx={{ color: palette.coral, p: 0.4 }}>
                                <Trash size={13} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>
          ) : (
          <>
          {/* ── Messages ── */}
          <Box ref={messagesBoxRef} sx={{
            flex: 1, overflowY: 'auto', px: 2, py: 1.5,
            background: palette.surface,
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-thumb': { background: 'rgba(184,137,44,0.28)', borderRadius: '4px' },
          }}>
            {fetching ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                <CircularProgress size={24} sx={{ color: palette.accent }} />
              </Box>
            ) : (
              <>
                {hasOlderHistory && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
                    <Box
                      component="button"
                      onClick={loadOlderHistory}
                      disabled={loadingOlder}
                      sx={{
                        px: 1.4, py: 0.5, borderRadius: '20px', cursor: loadingOlder ? 'default' : 'pointer',
                        border: `1px solid ${palette.border}`, background: palette.bg,
                        fontSize: '0.72rem', color: palette.accent, fontWeight: 600,
                        display: 'flex', alignItems: 'center', gap: 0.7,
                        '&:hover': { background: loadingOlder ? palette.bg : palette.accentLight },
                      }}
                    >
                      {loadingOlder ? <CircularProgress size={12} sx={{ color: palette.accent }} /> : null}
                      {loadingOlder ? 'Loading…' : 'Load older messages'}
                    </Box>
                  </Box>
                )}
                {messages.map((msg, i) => (
                  <Bubble
                    key={msg._id || i}
                    msg={msg}
                    user={user}
                    palette={palette}
                    modulePaths={modulePaths}
                    onNavigate={handleNavigateToModule}
                  />
                ))}
                {awaitingFirst && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 2 }}>
                    <AIAvatar palette={palette} />
                    <Box sx={{ background: palette.aiBubble, border: `1px solid ${palette.border}`, borderRadius: '16px', px: 1.5, py: 0.8, boxShadow: '0 2px 8px rgba(122,87,18,0.07)' }}>
                      <TypingDots palette={palette} />
                    </Box>
                  </Box>
                )}
              </>
            )}
            <div ref={bottomRef} />
          </Box>

          {/* ── Suggested questions ── */}
          {messages.length <= 1 && !loading && (
            <Box sx={{ px: 1.5, pb: 1, background: palette.surface, display: 'flex', flexWrap: 'wrap', gap: 0.7 }}>
              {suggestedQuestions.map(q => (
                <Box
                  key={q}
                  onClick={() => { setInput(q); setTimeout(handleSend, 50); }}
                  sx={{
                    px: 1.2, py: 0.5, borderRadius: '20px', cursor: 'pointer',
                    border: `1px solid ${palette.border}`, background: palette.accentLight,
                    fontSize: '0.70rem', color: palette.accent, fontWeight: 600,
                    transition: 'all 0.15s',
                    '&:hover': { background: palette.accentGlow, borderColor: palette.accent },
                  }}
                >
                  {q}
                </Box>
              ))}
            </Box>
          )}

          {/* ── Input — cream pill: sparkle · text · gold send button ── */}
          <Box sx={{ px: 1.6, pt: 1, pb: 1.6, background: palette.surface, flexShrink: 0 }}>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              pl: 1.6, pr: 0.7, py: 0.6,
              borderRadius: '999px',
              background: palette.bg,
              border: `1.5px solid ${palette.gold}`,
              boxShadow: '0 4px 14px rgba(184,137,44,0.16)',
              transition: 'box-shadow 0.18s, border-color 0.18s',
              '&:focus-within': { borderColor: palette.accent, boxShadow: `0 0 0 3px ${palette.accentGlow}` },
            }}>
              <Sparkle size={20} weight="fill" color={palette.accent} style={{ flexShrink: 0 }} />
              <TextField
                inputRef={inputRef}
                fullWidth
                multiline
                maxRows={3}
                variant="standard"
                placeholder="Ask me anything about MarketSync..."
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKey}
                disabled={loading}
                InputProps={{ disableUnderline: true }}
                sx={{
                  '& .MuiInputBase-root': { fontSize: '0.86rem', color: palette.text, py: 0.5 },
                  '& textarea': { color: `${palette.text} !important` },
                  '& textarea::placeholder': { color: palette.muted, opacity: 0.85 },
                }}
              />
              <IconButton
                onClick={handleSend}
                disabled={!input.trim() || loading}
                sx={{
                  width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                  background: input.trim() && !loading ? palette.goldBtn : 'rgba(184,137,44,0.22)',
                  color: '#fff',
                  boxShadow: input.trim() && !loading ? '0 4px 12px rgba(111,82,18,0.35)' : 'none',
                  transition: 'all 0.18s',
                  '&:hover': { transform: 'scale(1.06)', background: palette.goldBtn },
                  '&.Mui-disabled': { color: '#fff', opacity: 0.7 },
                }}
              >
                {loading
                  ? <CircularProgress size={16} sx={{ color: '#fff' }} />
                  : <PaperPlaneTilt size={18} weight="fill" />
                }
              </IconButton>
            </Box>
          </Box>
          </>
          )}

          {/* ── Docked: drag the left edge to resize the panel (like a side panel) ── */}
          {dockedNow && (
            <Box
              onMouseDown={onDockResizeDown}
              title="Drag to resize"
              sx={{
                position: 'absolute', left: 0, top: 0, bottom: 0, width: 10, zIndex: 25,
                cursor: 'ew-resize', display: 'flex', alignItems: 'center', justifyContent: 'center',
                '&:hover > div, &:active > div': { background: 'rgba(184,137,44,0.85)' },
              }}
            >
              <Box sx={{ width: 3, height: 44, borderRadius: '3px', background: 'rgba(184,137,44,0.35)', transition: 'background .15s' }} />
            </Box>
          )}

          {/* ── Resize handle (bottom-right corner) — desktop only, would let the panel grow past the viewport on mobile ── */}
          {!isMobile && !minimized && !dockedNow && (
            <Box
              onMouseDown={onResizeDown}
              title="Drag to resize"
              sx={{
                position: 'absolute', bottom: 0, right: 0,
                width: 22, height: 22,
                cursor: 'nwse-resize',
                display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end',
                p: '5px',
                zIndex: 20,
              }}
            >
              {/* Two-line resize grip */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', transform: 'rotate(-45deg)' }}>
                <Box sx={{ width: 10, height: '1.5px', background: 'rgba(184,137,44,0.5)', borderRadius: '2px' }} />
                <Box sx={{ width: 6,  height: '1.5px', background: 'rgba(184,137,44,0.35)', borderRadius: '2px' }} />
              </Box>
            </Box>
          )}

        </Box>
      </Fade>

      <ConfirmDialog
        open={!!deleteTarget}
        severity="delete"
        title="Delete this chat?"
        message={`"${deleteTarget?.title || ''}" and its entire message history will be permanently deleted. This can't be undone.`}
        loading={deletingConvo}
        onConfirm={confirmDeleteConversation}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}
